import { useEffect, useMemo, useState } from "react";

import type { MediaLightboxItem } from "~/shared/ui/media-lightbox";
import { useDelayedFlag } from "~/shared/hooks/useDelayedFlag";

import {
    type PickerTabValue,
    filterImagesByTab,
    findLightboxIndex,
    toLightboxImages,
} from "./pickerDialogModel";
import type { PickerImage, UseAttachmentPickerResult } from "./useAttachmentPicker";

export interface PickerDialogState {
    /** Спиннер показываем только если ответ длится >200ms (анти-мерцание). */
    showSpinner: boolean;
    /** Tabs показываем только если режим принимает ≥2 kind'а. */
    showTabs: boolean;
    tab: PickerTabValue;
    setTab: (tab: PickerTabValue) => void;
    filteredImages: PickerImage[];
    lightboxImages: MediaLightboxItem[];
    lightboxIndex: number | null;
    lightboxOpen: boolean;
    setLightboxIndex: (index: number | null) => void;
    handlePreview: (image: PickerImage) => void;
}

// View-model хук диалога: вся state/derivation-логика, отделённая от
// layout-JSX. Pure-вычисления вынесены в pickerDialogModel (тестируются
// отдельно), здесь — только React-state + эффекты + мемоизация.
export function usePickerDialogState(open: boolean, picker: UseAttachmentPickerResult): PickerDialogState {
    const { images, isLoading, clear, acceptedKinds } = picker;

    const showSpinner = useDelayedFlag(isLoading, 200);

    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const [tab, setTab] = useState<PickerTabValue>("all");

    useEffect(() => {
        if (!open) {
            clear();
            setLightboxIndex(null);
            setTab("all");
        }
    }, [open, clear]);

    const showTabs = acceptedKinds.length > 1;

    const filteredImages = useMemo(
        () => filterImagesByTab(images, tab, acceptedKinds),
        [images, tab, acceptedKinds],
    );

    const lightboxImages = useMemo<MediaLightboxItem[]>(
        () => toLightboxImages(filteredImages),
        [filteredImages],
    );

    const handlePreview = (img: PickerImage) => {
        if (img.kind !== "image") return;
        const idx = findLightboxIndex(lightboxImages, img.s3Url);
        if (idx !== null) setLightboxIndex(idx);
    };

    return {
        showSpinner,
        showTabs,
        tab,
        setTab,
        filteredImages,
        lightboxImages,
        lightboxIndex,
        lightboxOpen: lightboxIndex !== null,
        setLightboxIndex,
        handlePreview,
    };
}
